import {
  TextField,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Checkbox,
  FormLabel,
  InputAdornment,
  Chip,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

// Generic field type that works for both ProjectField and PublicProjectField
export interface FormFieldData {
  id?: string;
  name: string;
  label: string;
  type: string;
  required: boolean;
  multiple: boolean;
  order: number;
  help_text?: string | null;
  description?: string | null;
  unique?: boolean;
  display_label?: boolean;
  confirm?: boolean;
}

interface FormFieldProps {
  field: FormFieldData;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  textColor?: string;
  confirmValue?: string;
  confirmError?: string;
  onConfirmChange?: (value: string) => void;
}

// Reusable required asterisk component with red color and tooltip
const RequiredAsterisk = () => (
  <Tooltip title="Required" arrow placement="top">
    <span
      style={{
        color: "#d32f2f",
        fontWeight: "bold",
        cursor: "help",
        marginLeft: "4px",
      }}
    >
      *
    </span>
  </Tooltip>
);

function FormField({
  field,
  value,
  error,
  onChange,
  textColor,
  confirmValue,
  confirmError,
  onConfirmChange,
}: FormFieldProps) {
  // When textColor is undefined, use theme defaults (for admin interface)
  const useThemeDefaults = textColor === undefined;
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [emailValues, setEmailValues] = useState<string[]>(
    value ? value.split(",").map((v) => v.trim()) : [""],
  );
  const [newEmail, setNewEmail] = useState("");

  // Helper function to lighten/darken color for borders and secondary text
  const adjustColorOpacity = (color: string, opacity: number) => {
    // Convert hex to rgba
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Shared styles for all TextField components
  // When useThemeDefaults is true, let MUI handle all colors via the theme
  const textFieldStyles = {
    // Always style the required asterisk in red
    "& .MuiFormLabel-asterisk": {
      color: "#d32f2f",
      fontWeight: "bold",
    },
    // Only apply custom colors when textColor is provided (public forms)
    ...(useThemeDefaults
      ? {}
      : {
          "& .MuiInputLabel-root": {
            color: adjustColorOpacity(textColor!, 0.7),
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: textColor,
          },
          "& .MuiOutlinedInput-root": {
            color: textColor,
            "& fieldset": {
              borderColor: adjustColorOpacity(textColor!, 0.23),
            },
            "&:hover fieldset": {
              borderColor: adjustColorOpacity(textColor!, 0.5),
            },
            "&.Mui-focused fieldset": {
              borderColor: textColor,
            },
          },
          "& .MuiInputBase-input": {
            color: textColor,
          },
          "& .MuiFormHelperText-root": {
            color: adjustColorOpacity(textColor!, 0.6),
          },
        }),
  };

  // Styles for FormLabel
  const formLabelStyles = useThemeDefaults
    ? {}
    : {
        color: adjustColorOpacity(textColor!, 0.7),
        "&.Mui-focused": {
          color: textColor,
        },
      };

  const handleEmailChange = (index: number, newValue: string) => {
    const newValues = [...emailValues];
    newValues[index] = newValue;
    setEmailValues(newValues);
    const allValues = newEmail.trim()
      ? [...newValues, newEmail.trim()]
      : newValues;
    onChange(allValues.join(", "));
  };

  const handleAddEmail = () => {
    if (newEmail.trim()) {
      const newValues = [...emailValues, newEmail.trim()];
      setEmailValues(newValues);
      onChange(newValues.join(", "));
      setNewEmail("");
    }
  };

  const handleRemoveEmail = (index: number) => {
    const newValues = emailValues.filter((_, i) => i !== index);
    setEmailValues(newValues.length ? newValues : [""]);
    const allValues = newEmail.trim()
      ? [...newValues, newEmail.trim()]
      : newValues;
    onChange(allValues.join(", "));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleNewEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewEmail(value);
    const allValues = value.trim()
      ? [...emailValues, value.trim()]
      : emailValues;
    onChange(allValues.join(", "));
  };

  // Helper function to render the unique badge if the field is unique
  const renderUniqueAdornment = () => {
    if (field.unique) {
      return (
        <InputAdornment position="end">
          <Chip
            size="small"
            label="Unique"
            color="primary"
            variant="outlined"
            sx={{ fontSize: "0.7rem" }}
          />
        </InputAdornment>
      );
    }
    return null;
  };

  // Check if the error is a uniqueness error
  const isUniquenessError = error && error.includes("already exists");

  // Enhanced error display for uniqueness violations
  const getErrorHelperText = () => {
    if (isUniquenessError) {
      return (
        <>
          <span style={{ marginRight: 4 }}>⚠️</span>
          <span>{error}</span>
        </>
      );
    }
    return error || field.help_text;
  };

  // Render description modal
  const renderDescriptionModal = () => {
    if (!field.description) return null;

    return (
      <Dialog
        open={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{field.label}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              "& p": { mb: 2 },
              "& ul, & ol": { pl: 2, mb: 2 },
              "& li": { mb: 1 },
              "& a": {
                color: "#1976d2",
                textDecoration: "underline",
                cursor: "pointer",
                "&:hover": {
                  color: "#1565c0",
                  textDecoration: "underline",
                },
                "&:visited": {
                  color: "#7b1fa2",
                },
              },
            }}
            dangerouslySetInnerHTML={{ __html: field.description }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDescriptionModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Render help text with "read more" link if description exists
  const renderHelpText = () => {
    const helpText = getErrorHelperText();

    // If there's no help text but there is a description, show only the "Read more" link
    if (!helpText && field.description) {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDescriptionModalOpen(true);
          }}
          sx={{ whiteSpace: "nowrap" }}
        >
          Read more
        </Link>
      );
    }

    if (!helpText) return null;

    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <span>{helpText}</span>
        {field.description && (
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDescriptionModalOpen(true);
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            Read more
          </Link>
        )}
      </span>
    );
  };

  if (field.type === "email" && field.multiple) {
    return (
      <FormControl fullWidth error={!!error}>
        {field.display_label !== false && (
          <FormLabel sx={formLabelStyles}>
            {field.label}
            {field.required && <RequiredAsterisk />}
          </FormLabel>
        )}
        <Box sx={{ mt: 1 }}>
          {emailValues.map((email, index) => (
            <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                type="email"
                inputProps={{ inputMode: "email" }}
                value={email}
                onChange={(e) => handleEmailChange(index, e.target.value)}
                error={!!error}
                size="small"
                sx={textFieldStyles}
                InputProps={{ sx: { minHeight: 48 } }}
              />
              {index > 0 && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveEmail(index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              type="email"
              inputProps={{ inputMode: "email" }}
              value={newEmail}
              onChange={handleNewEmailChange}
              onKeyPress={handleKeyPress}
              placeholder="Add another email"
              size="small"
              sx={textFieldStyles}
              InputProps={{ sx: { minHeight: 48 } }}
            />
            <IconButton
              size="small"
              onClick={handleAddEmail}
              color="primary"
              disabled={!newEmail.trim()}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
        {field.help_text && (
          <FormHelperText
            sx={
              useThemeDefaults
                ? {}
                : { color: adjustColorOpacity(textColor!, 0.6) }
            }
          >
            {field.help_text}
          </FormHelperText>
        )}
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }

  // Helper to render label with required asterisk
  // display_label defaults to true if not specified (for public forms)
  const renderLabel = () => {
    if (field.display_label === false) return undefined;
    if (!field.required) return field.label;
    return (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {field.label}
        <RequiredAsterisk />
      </span>
    );
  };

  switch (field.type) {
    case "email":
      return (
        <>
          <TextField
            label={renderLabel()}
            type="email"
            inputProps={{ inputMode: "email" }}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={!!error}
            helperText={renderHelpText()}
            fullWidth
            sx={textFieldStyles}
            InputProps={{
              endAdornment: renderUniqueAdornment(),
              sx: {
                minHeight: 48,
                ...(isUniquenessError
                  ? { backgroundColor: "rgba(255, 0, 0, 0.03)" }
                  : {}),
              },
            }}
          />
          {field.confirm && onConfirmChange && (
            <TextField
              label="Confirm email"
              type="email"
              inputProps={{ inputMode: "email" }}
              value={confirmValue || ""}
              onChange={(e) => onConfirmChange(e.target.value)}
              error={!!confirmError}
              helperText={confirmError || undefined}
              fullWidth
              sx={{ ...textFieldStyles, mt: 2 }}
              InputProps={{ sx: { minHeight: 48 } }}
            />
          )}
          {renderDescriptionModal()}
        </>
      );
    case "phone":
      return (
        <>
          <TextField
            label={renderLabel()}
            type="tel"
            inputProps={{ inputMode: "tel" }}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={!!error}
            helperText={renderHelpText()}
            fullWidth
            sx={textFieldStyles}
            InputProps={{
              endAdornment: renderUniqueAdornment(),
              sx: {
                minHeight: 48,
                ...(isUniquenessError
                  ? { backgroundColor: "rgba(255, 0, 0, 0.03)" }
                  : {}),
              },
            }}
          />
          {renderDescriptionModal()}
        </>
      );
    case "boolean":
      return (
        <>
          <FormControl
            error={!!error}
            component="fieldset"
            sx={{
              m: 3,
              ...(isUniquenessError
                ? {
                    backgroundColor: "rgba(255, 0, 0, 0.03)",
                    p: 2,
                    borderRadius: 1,
                  }
                : {}),
            }}
            variant="standard"
          >
            {field.display_label !== false && (
              <FormLabel component="legend" sx={formLabelStyles}>
                {field.label}
                {field.required && <RequiredAsterisk />}
                {field.unique && (
                  <Chip
                    size="small"
                    label="Unique"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1, fontSize: "0.7rem" }}
                  />
                )}
              </FormLabel>
            )}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value === "true"}
                    onChange={(e) =>
                      onChange(e.target.checked ? "true" : "false")
                    }
                    name={field.name}
                    sx={
                      useThemeDefaults
                        ? {}
                        : {
                            color: adjustColorOpacity(textColor!, 0.6),
                            "&.Mui-checked": {
                              color: textColor,
                            },
                          }
                    }
                  />
                }
                label={field.help_text || "J'accepte / I agree"}
                sx={
                  useThemeDefaults
                    ? {}
                    : {
                        "& .MuiFormControlLabel-label": {
                          color: adjustColorOpacity(textColor!, 0.8),
                        },
                      }
                }
              />
            </FormGroup>
            {field.description && (
              <FormHelperText>
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDescriptionModalOpen(true);
                  }}
                >
                  Read more
                </Link>
              </FormHelperText>
            )}
            {isUniquenessError ? (
              <FormHelperText error>
                <span style={{ marginRight: 4 }}>⚠️</span>
                <span>{error}</span>
              </FormHelperText>
            ) : error ? (
              <FormHelperText error>{error}</FormHelperText>
            ) : null}
          </FormControl>
          {renderDescriptionModal()}
        </>
      );
    case "text":
    case "number":
      return (
        <>
          <TextField
            label={renderLabel()}
            type={field.type === "number" ? "number" : "text"}
            inputProps={{ inputMode: field.type === "number" ? "numeric" : "text" }}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={!!error}
            helperText={renderHelpText()}
            fullWidth
            sx={textFieldStyles}
            InputProps={{
              endAdornment: renderUniqueAdornment(),
              sx: {
                minHeight: 48,
                ...(isUniquenessError
                  ? { backgroundColor: "rgba(255, 0, 0, 0.03)" }
                  : {}),
              },
            }}
          />
          {renderDescriptionModal()}
        </>
      );
    case "paragraph":
      return (
        <Typography
          variant="body1"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            ...(useThemeDefaults ? {} : { color: textColor }),
          }}
          dangerouslySetInnerHTML={{ __html: value }}
        >
          {}
        </Typography>
      );
    default:
      return null;
  }
}

export default FormField;
